import { Perimeter } from "@nova/protobuf"
import { generateGenericCases } from "../../generateCases"
import { Builder } from "../../sockets"
import {
    CreatePerimeterArgs,
    getDefaultPerimeterCreateArgs,
    UpdatePerimeterArgs,
} from "@nova/serializer/src/client/validators/cubes/perimeter"
import {
    addEntitySocket,
    deleteEntitySocket,
    replaceEntitySocket,
} from "features/entity-caching/project/slice"
import { v4 as randomUUID } from "uuid"
import { zoomToFirePerimeter } from "features/map/inspector-panel/fire-perimeters/module"
import { createAction as createReduxAction } from "@reduxjs/toolkit"
import { get } from "@nova/serializer/src/client/path"
import { selectSingleLayer } from "panels/content/side-panel/slice.new"
import { centroid, maybeSetProjectLocation } from "../projectLocation"

const {
    create: createPerimeter,
    delete: deletePerimeter,
    createAction,
    deleteAction,
} = generateGenericCases<
    Perimeter, // entity type
    CreatePerimeterArgs, // create args to send to server
    ["name", "polygons", "buffers", "outline", "stroke", "width", "createdBy", "parentId"], // create args required by action
    UpdatePerimeterArgs // update args
>("Perimeter")

const replaceAction = createReduxAction<{
    id: string
    updates: Partial<Pick<Perimeter, UpdatePerimeterArgs[number]>>
}>("replacePerimeter")

export const addPerimetersToBuilder = (builder: Builder) =>
    builder
        .addCase(createAction, (store, { payload: { data } }, socket) => {
            const optimisticData = {
                id: randomUUID(),
                ...getDefaultPerimeterCreateArgs(store.getState().project.data, data.parentId),
                ...data,
            }
            createPerimeter(socket, { ...data, id: optimisticData.id, index: optimisticData.index })
            store.dispatch(
                addEntitySocket({
                    data: optimisticData,
                    path: ["perimeters"],
                })
            )
            const project = store.getState().project.data
            const center = centroid(optimisticData.polygons[0]?.coordinates)
            maybeSetProjectLocation(store, project.orgId, project.id, center)

            store.dispatch(selectSingleLayer({ id: optimisticData.id }))
            zoomToFirePerimeter(optimisticData)
        })
        .addCase(replaceAction, (store, { payload: { updates, id } }, socket) => {
            const path = ["perimeters", { id }] as const
            const currentPerimeter = get(store.getState().project.data, path)
            const data = { ...currentPerimeter, ...updates }
            socket.emit("updatePerimeter", id, updates)
            store.dispatch(replaceEntitySocket({ data, path }))
        })
        .addCase(deleteAction, (store, { payload: { id } }, socket) => {
            deletePerimeter(socket, id)
            store.dispatch(deleteEntitySocket({ path: ["perimeters", { id }] }))
        })

export {
    createAction as createPerimeter,
    replaceAction as updatePerimeter,
    deleteAction as deletePerimeter,
}
